import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
var _excluded = ["ref"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { bind } from 'bind-event-listener';
import Button from '@atlaskit/button/standard-button';
import { KEY_DOWN } from '@atlaskit/ds-lib/keycodes';
import noop from '@atlaskit/ds-lib/noop';
import useControlledState from '@atlaskit/ds-lib/use-controlled';
import useFocus from '@atlaskit/ds-lib/use-focus-event';
import ExpandIcon from '@atlaskit/icon/glyph/chevron-down';
import Popup from '@atlaskit/popup';
import { gridSize as gridSizeFn, layers } from '@atlaskit/theme/constants';
import FocusManager from './internal/components/focus-manager';
import MenuWrapper from './internal/components/menu-wrapper';
import SelectionStore from './internal/context/selection-store';
import useGeneratedId from './internal/utils/use-generated-id';
var gridSize = gridSizeFn();
var MAX_HEIGHT = "calc(100vh - ".concat(gridSize * 2, "px)");
var opposites = {
  top: 'bottom',
  bottom: 'top',
  left: 'right',
  right: 'left',
  start: 'end',
  auto: 'auto',
  end: 'start'
};
var getFallbackPlacements = function getFallbackPlacements(placement) {
  var placementPieces = placement.split('-');
  var mainAxis = placementPieces[0];

  // Left, right and auto placements can rely on standard popper sliding behaviour
  if (!['top', 'bottom'].includes(mainAxis)) {
    return undefined;
  }

  // Top and bottom placements need to flip to the right/left to ensure
  // long lists don't extend off the screen
  else if (placementPieces.length === 2 && ['start', 'end'].includes(placementPieces[1])) {
    var crossAxis = placementPieces[1];
    return ["".concat(mainAxis), "".concat(mainAxis, "-").concat(opposites[crossAxis]), "".concat(opposites[mainAxis], "-").concat(crossAxis), "".concat(opposites[mainAxis]), "".concat(opposites[mainAxis], "-").concat(opposites[crossAxis]), 'auto'];
  } else {
    return ["".concat(mainAxis, "-start"), "".concat(mainAxis, "-end"), "".concat(opposites[mainAxis]), "".concat(opposites[mainAxis], "-start"), "".concat(opposites[mainAxis], "-end"), "auto"];
  }
};

/**
 * __Dropdown menu__
 *
 * A dropdown menu displays a list of actions or options to a user.
 *
 * - [Examples](https://atlassian.design/components/dropdown-menu/examples)
 * - [Code](https://atlassian.design/components/dropdown-menu/code)
 * - [Usage](https://atlassian.design/components/dropdown-menu/usage)
 */
var DropdownMenu = function DropdownMenu(props) {
  var _props$defaultOpen = props.defaultOpen,
    defaultOpen = _props$defaultOpen === void 0 ? false : _props$defaultOpen,
    isOpen = props.isOpen,
    _props$onOpenChange = props.onOpenChange,
    onOpenChange = _props$onOpenChange === void 0 ? noop : _props$onOpenChange,
    children = props.children,
    _props$placement = props.placement,
    placement = _props$placement === void 0 ? 'bottom-start' : _props$placement,
    _trigger = props.trigger,
    _props$shouldFlip = props.shouldFlip,
    shouldFlip = _props$shouldFlip === void 0 ? true : _props$shouldFlip,
    _props$isLoading = props.isLoading,
    isLoading = _props$isLoading === void 0 ? false : _props$isLoading,
    _props$autoFocus = props.autoFocus,
    autoFocus = _props$autoFocus === void 0 ? false : _props$autoFocus,
    testId = props.testId,
    statusLabel = props.statusLabel,
    _props$zIndex = props.zIndex,
    zIndex = _props$zIndex === void 0 ? layers.modal() : _props$zIndex;
  var _useControlledState = useControlledState(isOpen, function () {
      return defaultOpen;
    }),
    _useControlledState2 = _slicedToArray(_useControlledState, 2),
    isLocalOpen = _useControlledState2[0],
    setLocalIsOpen = _useControlledState2[1];
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isTriggeredUsingKeyboard = _useState2[0],
    setTriggeredUsingKeyboard = _useState2[1];
  var fallbackPlacements = useMemo(function () {
    return getFallbackPlacements(placement);
  }, [placement]);
  var handleTriggerClicked = useCallback(
  // TODO: event is an `any` and is being cast incorrectly
  // This means that the public type for `onOpenChange` is incorrect
  // current: (event: React.MouseEvent | React.KeyboardEvent) => void;
  // correct: (event: React.MouseEvent | KeyboardEvent) => void;
  // https://product-fabric.atlassian.net/browse/DSP-4692
  function (event) {
    var newValue = !isLocalOpen;
    var clientX = event.clientX,
      clientY = event.clientY,
      type = event.type;
    if (type === 'keydown') {
      setTriggeredUsingKeyboard(true);
    } else if (clientX === 0 || clientY === 0) {
      // Hitting enter/space is registered as a click
      // with both clientX and clientY === 0
      setTriggeredUsingKeyboard(true);
    }
    setLocalIsOpen(newValue);
    onOpenChange({
      isOpen: newValue,
      event: event
    });
  }, [onOpenChange, isLocalOpen, setLocalIsOpen]);
  var handleOnClose = useCallback(function (event) {
    var newValue = false;
    setLocalIsOpen(newValue);
    onOpenChange({
      isOpen: newValue,
      event: event
    });
  }, [onOpenChange, setLocalIsOpen]);
  var _useFocus = useFocus(),
    isFocused = _useFocus.isFocused,
    bindFocus = _useFocus.bindFocus;

  // When a trigger is focused, we want to open the dropdown if
  // the user presses the DownArrow
  useEffect(function () {
    // Only need to listen for keydown when focused
    if (!isFocused) {
      return noop;
    }

    // Being safe: we don't want to open the dropdown if it is already open
    // Note: This shouldn't happen as the trigger should not be able to get focus
    if (isLocalOpen) {
      return noop;
    }
    return bind(window, {
      type: 'keydown',
      listener: function openOnKeyDown(e) {
        if (e.key === KEY_DOWN) {
          // prevent page scroll
          e.preventDefault();
          handleTriggerClicked(e);
        }
      }
    });
  }, [isFocused, isLocalOpen, handleTriggerClicked]);
  var id = useGeneratedId();
  return /*#__PURE__*/React.createElement(SelectionStore, null, /*#__PURE__*/React.createElement(Popup, {
    id: isLocalOpen ? id : undefined,
    shouldFlip: shouldFlip,
    isOpen: isLocalOpen,
    onClose: handleOnClose,
    zIndex: zIndex,
    placement: placement,
    fallbackPlacements: fallbackPlacements,
    testId: testId && "".concat(testId, "--content"),
    shouldUseCaptureOnOutsideClick: true,
    trigger: function trigger(triggerProps) {
      if (typeof _trigger === 'function') {
        var ref = triggerProps.ref,
          providedProps = _objectWithoutProperties(triggerProps, _excluded);
        return _trigger(_objectSpread(_objectSpread(_objectSpread({}, providedProps), bindFocus), {}, {
          triggerRef: ref,
          isSelected: isLocalOpen,
          onClick: handleTriggerClicked,
          testId: testId && "".concat(testId, "--trigger")
        }));
      }
      return /*#__PURE__*/React.createElement(Button, _extends({}, bindFocus, {
        ref: triggerProps.ref,
        "aria-controls": triggerProps['aria-controls'],
        "aria-expanded": triggerProps['aria-expanded'],
        "aria-haspopup": triggerProps['aria-haspopup'],
        isSelected: isLocalOpen,
        iconAfter: /*#__PURE__*/React.createElement(ExpandIcon, {
          size: "medium",
          label: ""
        }),
        onClick: handleTriggerClicked,
        testId: testId && "".concat(testId, "--trigger")
      }), _trigger);
    },
    content: function content(_ref) {
      var setInitialFocusRef = _ref.setInitialFocusRef,
        update = _ref.update;
      return /*#__PURE__*/React.createElement(FocusManager, null, /*#__PURE__*/React.createElement(MenuWrapper, {
        maxHeight: MAX_HEIGHT,
        maxWidth: 800,
        onClose: handleOnClose,
        onUpdate: update,
        isLoading: isLoading,
        statusLabel: statusLabel,
        setInitialFocusRef: isTriggeredUsingKeyboard || autoFocus ? setInitialFocusRef : undefined
      }, children));
    }
  }));
};
export default DropdownMenu;